import { Badge } from '@/components/ui/Badge';
import { useMemo } from 'react';
import {
  WorkspaceMetricCell,
  WorkspaceMetricHeaderCells,
  WorkspaceMetricTotalsRow,
} from '@/components/workspace/WorkspaceMetricCell';
import { WorkspaceColumnHeader } from '@/components/workspace/WorkspaceColumnHeader';
import {
  workspaceColumnStickyProps,
  workspaceDraggedColumnClass,
  workspaceDropTargetClass,
} from '@/components/workspace/WorkspaceOrderedHeaders';
import { useWorkspaceFrozenLayout } from '@/components/workspace/workspaceStickyColumns';
import { totalsFromMetricRows } from '@/utils/workspaceAdProductMetrics';
import {
  SelectionCheckbox,
  WorkspaceCheckboxCell,
  WorkspaceMiddleCell,
  WorkspaceSection,
  WorkspaceTableShell,
  tableRowClass,
  workspaceTableClass,
} from '@/components/workspace/WorkspaceSection';
import { WorkspaceTableSelectionActions } from '@/components/workspace/WorkspaceTableSelectionActions';
import type { WorkspaceDimensionSelectionProps } from '@/components/workspace/workspaceSelectionTypes';
import { ReportColumnPicker } from '@/components/ReportColumnPicker';
import { WorkspaceExportButton } from '@/components/workspace/WorkspaceExportButton';
import { useColumnOrder, WORKSPACE_LABEL_LEADING_FREEZE_IDS } from '@/components/workspace/useColumnOrder';
import { metricColumnPickerOptions } from '@/components/workspace/workspaceColumnConfig';
import { useTableSort } from '@/components/workspace/useTableSort';
import { useAppDateRange } from '@/contexts/AppDateRangeContext';
import { exportMetricRollupCsv } from '@/api/workspaceCsvExport';
import { useToast } from '@/components/ui/Toast';
import { WorkspaceMetricFiltersPopover } from '@/components/workspace/WorkspaceMetricFiltersPopover';
import { useMetricFilters } from '@/components/workspace/useMetricFilters';
import { rowMatchesMetricFilters } from '@/utils/workspaceMetricFilters';
import {
  EMPTY_TARGETING_TYPE_METRICS,
  TARGETING_TYPE_METRIC_COLUMNS,
  TARGETING_TYPE_ROWS,
  type TargetingTypeDerivedMetrics,
  type TargetingTypeId,
} from '@/utils/workspaceTargetingTypeMetrics';

export function WorkspaceTargetingTypeMetricsTable({
  current,
  previous,
  compareEnabled,
  isLoading,
  rangeSubtitle,
  isFiltered,
  selection,
}: {
  current: Map<TargetingTypeId, TargetingTypeDerivedMetrics>;
  previous: Map<TargetingTypeId, TargetingTypeDerivedMetrics>;
  currentTotals?: TargetingTypeDerivedMetrics;
  previousTotals?: TargetingTypeDerivedMetrics;
  compareEnabled: boolean;
  isLoading: boolean;
  rangeSubtitle: string;
  isFiltered: boolean;
  selection: WorkspaceDimensionSelectionProps;
}) {
  const { showToast } = useToast();
  const { range } = useAppDateRange();
  const exportStart = range.current.startDate;
  const exportEnd = range.current.endDate;
  const tableSort = useTableSort({ defaultSortBy: 'cost', defaultSortDir: 'desc' });
  const metricFilters = useMetricFilters({
    tableId: 'targeting-type-metrics',
    metrics: TARGETING_TYPE_METRIC_COLUMNS,
  });
  const columnOrder = useColumnOrder(
    TARGETING_TYPE_METRIC_COLUMNS.map((column) => column.key),
    undefined,
    WORKSPACE_LABEL_LEADING_FREEZE_IDS
  );
  const freezeLayout = useWorkspaceFrozenLayout(
    columnOrder.frozenColumnIds,
    columnOrder.displayOrder.join('\0')
  );
  const subtitleParts = [rangeSubtitle];
  if (isFiltered) subtitleParts.push('Filtered to selected entities');

  const rows = useMemo(
    () =>
      TARGETING_TYPE_ROWS.map(({ id, label }) => ({
        id,
        label,
        metrics: current.get(id) ?? EMPTY_TARGETING_TYPE_METRICS,
        prevMetrics: previous.get(id) ?? EMPTY_TARGETING_TYPE_METRICS,
      })).filter((row) => rowMatchesMetricFilters(row.metrics, metricFilters.completeFilters))
      .sort((left, right) => {
        if (tableSort.sortBy === 'label') {
          const cmp = left.label.localeCompare(right.label, undefined, { sensitivity: 'base' });
          return tableSort.sortDir === 'asc' ? cmp : -cmp;
        }
        const cmp =
          (left.metrics as Record<string, number>)[tableSort.sortBy] -
          (right.metrics as Record<string, number>)[tableSort.sortBy];
        return tableSort.sortDir === 'asc' ? cmp : -cmp;
      }),
    [current, previous, metricFilters.completeFilters, tableSort.sortBy, tableSort.sortDir]
  );

  const rowIds = rows.map((row) => row.id);
  const allSelected =
    rowIds.length > 0 && rowIds.every((id) => selection.pendingIds.includes(id));

  const { currentTotals, previousTotals } = useMemo(() => {
    return {
      currentTotals: totalsFromMetricRows(rows.map((row) => row.metrics)),
      previousTotals: totalsFromMetricRows(rows.map((row) => row.prevMetrics)),
    };
  }, [rows]);

  return (
    <WorkspaceSection
      title="Targeting Type Breakdown"
      countLabel={subtitleParts.join(' · ')}
      actions={
        <>
          <WorkspaceMetricFiltersPopover metrics={TARGETING_TYPE_METRIC_COLUMNS} {...metricFilters} />
          <WorkspaceTableSelectionActions
            entityLabel="targeting type"
            selectionCount={selection.selectionCount}
            hasPendingChanges={selection.hasPendingChanges}
            canApply={selection.canApply}
            onApply={selection.onApply}
            onClearSelection={selection.onClearSelection}
          />
          <WorkspaceExportButton
            onExport={() => {
              exportMetricRollupCsv({
                filenamePrefix: 'workspace_targeting_type_metrics',
                startDate: exportStart,
                endDate: exportEnd,
                labelHeader: 'Targeting Type',
                visibleMetricIds: columnOrder.visibleIds,
                metricColumns: TARGETING_TYPE_METRIC_COLUMNS,
                rows,
              });
              showToast(`Exported ${rows.length} rows`, 'success');
            }}
          />
          <ReportColumnPicker
            columns={metricColumnPickerOptions(TARGETING_TYPE_METRIC_COLUMNS)}
            selected={columnOrder.visibleIds}
            onChange={columnOrder.setVisibleIds}
            panelTitle="Visible columns"
          />
        </>
      }
    >
      <WorkspaceTableShell
        isEmpty={rows.length === 0}
        emptyMessage="Try adjusting metric filters, or clear them to see all targeting types."
        isLoading={isLoading}
      >
        <table ref={freezeLayout.tableRef} className={workspaceTableClass.table}>
          <thead>
            <tr>
              <WorkspaceCheckboxCell isHeader>
                <SelectionCheckbox
                  checked={allSelected}
                  label="Select all targeting types"
                  onChange={(checked) => selection.onToggleAll(rowIds, checked)}
                />
              </WorkspaceCheckboxCell>
              <WorkspaceColumnHeader
                label="Targeting Type"
                columnId="label"
                sortBy={tableSort.sortBy}
                sortDir={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                movable={false}
                isFrozen={false}
                className={workspaceColumnStickyProps('label', columnOrder.frozenColumnIds, freezeLayout.frozenLeftById, 'header').className}
                style={workspaceColumnStickyProps('label', columnOrder.frozenColumnIds, freezeLayout.frozenLeftById, 'header').style}
              />
              <WorkspaceMetricHeaderCells
                columns={columnOrder.displayOrder
                  .map((columnId) => TARGETING_TYPE_METRIC_COLUMNS.find((item) => item.key === columnId))
                  .filter((item): item is (typeof TARGETING_TYPE_METRIC_COLUMNS)[number] => Boolean(item))}
                sortable
                movable
                sortBy={tableSort.sortBy}
                sortDir={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                onFreeze={columnOrder.freezeColumn}
                frozenColumnIds={columnOrder.frozenColumnIds}
                pinnedFrozenColumnId={columnOrder.pinnedFrozenColumnId}
                frozenLeftById={freezeLayout.frozenLeftById}
                onDragStartColumn={columnOrder.startDrag}
                onDragHoverColumn={columnOrder.hoverReorder}
                onDropColumn={columnOrder.commitDrop}
                onDragEndColumn={columnOrder.endDrag}
                activeDragColumnId={columnOrder.activeDragColumnId}
                hoverColumnId={columnOrder.hoverColumnId}
                hoverPosition={columnOrder.hoverPosition}
                hoverInsertIndex={columnOrder.hoverInsertIndex}
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map(({ id, label, metrics, prevMetrics }) => {
              const selected = selection.pendingIds.includes(id);
              return (
                <tr key={id} className={tableRowClass(selected)}>
                  <WorkspaceCheckboxCell>
                    <SelectionCheckbox
                      checked={selected}
                      label={`Select targeting type ${label}`}
                      onChange={(checked) => selection.onToggleId(id, checked)}
                    />
                  </WorkspaceCheckboxCell>
                  <WorkspaceMiddleCell
                    className={`${workspaceColumnStickyProps('label', columnOrder.frozenColumnIds, freezeLayout.frozenLeftById).className} ${columnOrder.activeDragColumnId === 'label' ? workspaceDraggedColumnClass(true) : ''}`}
                    style={workspaceColumnStickyProps('label', columnOrder.frozenColumnIds, freezeLayout.frozenLeftById).style}
                  >
                    <Badge variant="neutral">{label}</Badge>
                  </WorkspaceMiddleCell>
                  {columnOrder.displayOrder.map((columnId) => {
                    const def = TARGETING_TYPE_METRIC_COLUMNS.find((item) => item.key === columnId);
                    if (!def) return null;
                    const isDragged = columnOrder.activeDragColumnId === columnId;
                    const isDropTarget =
                      columnOrder.hoverColumnId === columnId &&
                      columnOrder.activeDragColumnId !== columnId;
                    const sticky = workspaceColumnStickyProps(
                      columnId,
                      columnOrder.frozenColumnIds,
                      freezeLayout.frozenLeftById
                    );
                    return (
                    <WorkspaceMetricCell
                      key={def.key}
                      def={def}
                      current={metrics[def.key]}
                      previous={prevMetrics[def.key]}
                      compareEnabled={compareEnabled}
                      isLoading={isLoading}
                      className={`${sticky.className} ${workspaceDraggedColumnClass(isDragged)} ${workspaceDropTargetClass(isDropTarget, columnOrder.hoverPosition)}`}
                      style={sticky.style}
                    />
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <WorkspaceMetricTotalsRow
              labelColSpan={2}
              frozenColumnIds={columnOrder.frozenColumnIds}
              frozenLeftById={freezeLayout.frozenLeftById}
              current={currentTotals}
              previous={previousTotals}
              compareEnabled={compareEnabled}
              isLoading={isLoading}
              columns={columnOrder.displayOrder
                .map((columnId) => TARGETING_TYPE_METRIC_COLUMNS.find((item) => item.key === columnId))
                .filter((item): item is (typeof TARGETING_TYPE_METRIC_COLUMNS)[number] => Boolean(item))}
            />
          </tfoot>
        </table>
      </WorkspaceTableShell>
    </WorkspaceSection>
  );
}
